import * as yup from 'yup';
import { __, isValidId, checkIfOnlyEsim } from '@/utils';
 
export const getValidationShema = () => {
    const validationSchema = [
        yup.object({
            phoneNumbers: yup.array()
                .of(
                    yup.object().shape({
                        number: yup.string().required('Phone number is required')
                    })
                )
                .min(1, 'At least one phone number is required')
        }),
        yup.object({
            activeTab: yup.string(),
            subscriberSendSimToAddress: yup.bool(),
            companySendSimToAddress: yup.bool(),
            subscriberAddressType: yup.string(),
            companyAddressType: yup.string(),
            subscriberFirstName: yup.string().when('activeTab', {
                is: 'Private Client',
                then: schema => schema.required(__('At least two characters must be entered. Letters, hyphens, and slashes are allowed.')),
            }),
            subscriberShippingSettlement: yup.string().when(['activeTab', 'subscriberSendSimToAddress'], {
                is: (activeTab, subscriberSendSimToAddress) => activeTab === 'Private Client' && subscriberSendSimToAddress === false,
                then: schema => schema.required(__('Required field')),
            }),
            subscriberShippingNumberHome: yup.string().when(['activeTab', 'subscriberSendSimToAddress'], {
                is: (activeTab, subscriberSendSimToAddress) => activeTab === 'Private Client' && subscriberSendSimToAddress === false,
                then: schema => schema.required(__('Required field')),
            }),

            subscriberShippingEntrance: yup.string().when(['activeTab', 'subscriberSendSimToAddress', 'subscriberShippingZipStatus'], {
                is: (activeTab, subscriberSendSimToAddress, value) => activeTab === 'Private Client' && subscriberSendSimToAddress === false && value === 'address_missing_entrance',
                then: schema => schema.required(__('Required field')),
            }),

            subscriberEntrance: yup.string().when('subscriberZipStatus', {
                is: 'address_missing_entrance',
                then: schema => schema.required(__('Required field'))
            }),

            subscriberZipStatus: yup.string().test('isCorrectValidEntrance', __('A valid entracne must be entered'), value => value !== 'address_missing_entrance' || !value),

            subscriberShippingZipStatus: yup.string().when(['activeTab', 'subscriberSendSimToAddress'], {
                is: (activeTab, subscriberSendSimToAddress, value) => activeTab === 'Private Client' && subscriberSendSimToAddress === false,
                then: schema => schema
                .test('isCorrectShippingValidEntrance', __('A valid entracne must be entered'), value => value !== 'address_missing_entrance' || !value),
                otherwise: schema => schema.nullable(),
            }),

            subscriberShippingStreet: yup.string().when(['activeTab', 'subscriberSendSimToAddress'], {
                is: (activeTab, subscriberSendSimToAddress) => activeTab === 'Private Client' && subscriberSendSimToAddress === false,
                then: schema => schema.required(__('Required field')),
            }),
            subscriberShippingCityCode: yup.string().when(['activeTab', 'subscriberSendSimToAddress'], {
                is: (activeTab, subscriberSendSimToAddress) => activeTab === 'Private Client' && subscriberSendSimToAddress === false,
                then: schema => schema.required(__('Wrong Address')),
            }),
            subscriberShippingStreetCode: yup.string().when(['activeTab', 'subscriberSendSimToAddress'], {
                is: (activeTab, subscriberSendSimToAddress) => activeTab === 'Private Client' && subscriberSendSimToAddress === false,
                then: schema => schema.required(__('Wrong Address')),
            }),
            subscriberPO: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byPO',
                then: schema => schema.required(__('Required field')),
            }),
            subscriberPOSettlement: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byPO',
                then: schema => schema.required(__('Required field')),
            }),
            subscriberSettlement: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            subscriberStreet: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            subscriberCityCode: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byAddress',
                then: schema => schema.required(__('Wrong Address')),
            }),
            subscriberStreetCode: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byAddress',
                then: schema => schema.required(__('Wrong Address')),
            }),
            subscriberNumberHome: yup.string().when(['activeTab', 'subscriberAddressType'], {
                is: (activeTab, subscriberAddressType) => activeTab === 'Private Client' && subscriberAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),

            subscriberLastName: yup.string().when('activeTab', {
                is: 'Private Client',
                then: schema => schema.required(__('At least two characters must be entered. Letters, hyphens, and slashes are allowed.')),
            }),
            subscriberID: yup.string().when('activeTab', {
                is: 'Private Client',
                then: schema => schema
                    .required(__('A valid Israeli ID number must be entered, including a check digit'))
                    .test('isValidId', __('A valid Israeli ID number must be entered, including a check digit'), value => isValidId(value))
                    .test('isCorrectLength', __('The ID number must be 9 digits long (including zeros)'), value => value && value.length === 9),
                    otherwise: schema => schema.nullable(),
            }),
            subscriberEmail: yup.string().when('activeTab', {
                is: 'Private Client',
                then: schema => schema.
                required(__('A valid email address must be entered'))
                .test(
                    'valid-email-format',
                    'The email must have a valid format (e.g., example@domain.com)',
                    (value) => {
                        if (!value) return false;
        
                        const [localPart, domainPart] = value.split('@');
                        if (!localPart || !domainPart) return false;
            
                        const domainSegments = domainPart.split('.');
                        const isValidDomain = domainSegments.length > 1 && domainSegments.pop().length >= 2;
            
                        return isValidDomain;
                    }
                )
                .email(__('A valid email address must be entered')),
            }),
            subscriberPhone: yup.string().when('activeTab', {
                is: 'Private Client',
                then: schema => schema.required(__('A valid Israeli number must be entered')).length(11, __('Phone number must be exactly 10 characters')),
            }),
            // zipCode: yup.string().when('activeTab', {
            //     is: 'Private Client',
            //     then: schema => schema.required(__('No ZipCode for Address')),
            // }),
            // shippingzipCode: yup.string().when('subscriberSendSimToAddress', {
            //     is: (activeTab) => (activeTab === 'Private Client' && !subscriberSendSimToAddress ),
            //     then: schema => schema.required(__('No ZipCode for Address')),
            // }),
            shippingzipCode: yup.string().when('companySendSimToAddress', {
                is: (activeTab) => (activeTab === 'Business Client' && !companySendSimToAddress),
                then: schema => schema.required(__('No ZipCode for Address')),
            }),
            subscriberShippingMethod: yup.string().when('activeTab', {
                is: (activeTab, context) => activeTab === 'Private Client' && !checkIfOnlyEsim(),
                then: schema => schema.required(__('Required field')),
            }),
            companyFirstName: yup.string().when('activeTab', {
                is: 'Business Client',
                then: schema => schema.required(__('At least two characters must be entered. Letters, hyphens, and slashes are allowed.')),
            }),
            companyLastName: yup.string().when('activeTab', {
                is: 'Business Client',
                then: schema => schema.required(__('At least two characters must be entered. Letters, hyphens, and slashes are allowed.')),
            }),
            companyName: yup.string().when('activeTab', {
                is: (activeTab) => activeTab === 'Business Client',
                then: schema => schema.required(__('Required field')),
            }),
            companyID: yup.string().when('activeTab', {
                is: 'Business Client',
                then: schema => schema
                    .required(__('A valid Israeli ID number must be entered, including a check digit'))
                    .test('isValidId', __('A valid Israeli ID number must be entered, including a check digit'), value => isValidId(value))
                    .test('isCorrectLength', __('The ID number must be 9 digits long (including zeros)'), value => value && value.length === 9),
                otherwise: schema => schema.nullable(),
            }),
            comapnyHpNumber: yup.number().typeError(__('Required field')).when('activeTab', {
                is: 'Business Client',
                then: schema => schema.required(__('Required field')),
            }),
            companyPhone: yup.string().when('activeTab', {
                is: 'Business Client',
                then: schema => schema.required(__('A valid Israeli number must be entered')).length(11, __('Phone number must be exactly 10 characters')),
            }),
            companyEmail: yup.string().when('activeTab', {
                is: 'Business Client',
                then: schema => schema.required(__('Required field')).email(__('Required field')),
            }),
            companyPO: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byPO',
                then: schema => schema.required(__('Required field')),
            }),
            companyPOSettlement: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byPO',
                then: schema => schema.required(__('Required field')),
            }),
            companySettlement: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            companyStreet: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            companyCityCode: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            companyStreetCode: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            companyNumberHome: yup.string().when(['activeTab', 'companyAddressType'], {
                is: (activeTab, companyAddressType) => activeTab === 'Business Client' && companyAddressType === 'byAddress',
                then: schema => schema.required(__('Required field')),
            }),
            companyShippingMethod: yup.string().when('activeTab', {
                is: (activeTab, context) => activeTab === 'Business Client' && !checkIfOnlyEsim(),
                then: schema => schema.required(__('Required field')),
            }),
            companyShippingSettlement: yup.string().when(['activeTab', 'companySendSimToAddress'], {
                is: (activeTab, companySendSimToAddress) => activeTab === 'Business Client' && companySendSimToAddress === false,
                then: schema => schema.required(__('Required field')),
            }),
            companyShippingNumberHome: yup.string().when(['activeTab', 'companySendSimToAddress'], {
                is: (activeTab, companySendSimToAddress) => activeTab === 'Business Client' && companySendSimToAddress === false,
                then: schema => schema.required(__('Required field')),
            }),
            companyShippingStreet: yup.string().when(['activeTab', 'companySendSimToAddress'], {
                is: (activeTab, companySendSimToAddress) => activeTab === 'Business Client' && companySendSimToAddress === false,
                then: schema => schema.required(__('Required field')),
            }),
            companyShippingCityCode: yup.string().when(['activeTab', 'companySendSimToAddress'], {
                is: (activeTab, companySendSimToAddress) => activeTab === 'Business Client' && companySendSimToAddress === false,
                then: schema => schema.required(__('Wrong Address')),
            }),
            companyShippingStreetCode: yup.string().when(['activeTab', 'companySendSimToAddress'], {
                is: (activeTab, companySendSimToAddress) => activeTab === 'Business Client' && companySendSimToAddress === false,
                then: schema => schema.required(__('Wrong Address')),
            }),
        }),
        yup.object({
            terms: yup.bool().required(__('Required field')),
        }),
        yup.object({
        }),
    ];

    return validationSchema;
}